import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import SEO from '../components/ui/SEO';
import Button from '../components/ui/Button';
import { callsToAction, turnaround } from '../data/practice';
const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: {
    '@type': 'Person',
    name: 'Dr Imtiaz Syed',
    jobTitle: 'Independent Psychiatric Expert Witness',
    url: 'https://medicolegal-psychiatry.co.uk/about',
    image: 'https://cdn.magicpatterns.com/uploads/vPTXagdmQWKZaxnhPCE2i5/linked_in.jpg',
    email: 'enquiries@medicolegal-psychiatry.co.uk',
    description: 'Independent Psychiatric Expert Witness (MBBS, MBA) providing medico-legal psychiatric assessments and reports for proceedings in England and Wales.',
    worksFor: {
      '@type': 'Organization',
      name: 'Medicolegal Psychiatry',
      url: 'https://medicolegal-psychiatry.co.uk'
    },
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'England and Wales'
    },
    knowsAbout: ['Fitness to plead and criminal psychiatric reports', 'Mental and litigation capacity', 'Immigration and asylum psychiatric reports', 'PTSD and complex trauma', 'Clinical negligence and personal injury psychiatry', 'ADHD and neurodevelopmental assessment']
  }
};
const practiceAreas = [{
  label: 'Criminal psychiatric reports and fitness to plead',
  path: '/services/forensic-risk#criminal'
}, {
  label: 'Parole Board and licence-condition assessments',
  path: '/services/forensic-risk#parole'
}, {
  label: 'Mental and litigation capacity',
  path: '/services/mental-capacity#litigation-capacity'
}, {
  label: 'Court of Protection, deputyship and lasting powers of attorney',
  path: '/services/mental-capacity#court-of-protection'
}, {
  label: 'Testamentary and retrospective capacity',
  path: '/services/mental-capacity#testamentary'
}, {
  label: 'Immigration, asylum and human rights',
  path: '/services/specialist#immigration'
}, {
  label: 'PTSD and complex trauma',
  path: '/services/trauma-ptsd'
}, {
  label: 'Clinical negligence and personal injury',
  path: '/services/clinical-negligence'
}, {
  label: 'Employment and professional proceedings',
  path: '/services/specialist#employment'
}, {
  label: 'Family proceedings',
  path: '/services/specialist#family'
}, {
  label: 'Extradition',
  path: '/services/forensic-risk#extradition'
}, {
  label: 'ADHD and neurodevelopmental assessments',
  path: '/services/neurodevelopmental'
}];
const approach = [{
  title: 'Answering the questions asked',
  text: 'Each report is structured around the questions in the letter of instruction, so the reader can find the answer to every question without searching for it.'
}, {
  title: 'Reviewing the evidence',
  text: 'Medical records, previous reports and the documents provided are reviewed alongside the clinical assessment. Facts, the history given and clinical findings are kept distinct from opinion.'
}, {
  title: 'Explaining the reasoning',
  text: 'Opinions are explained, not just stated. The report sets out how the evidence leads to each conclusion, so it can be tested and relied on.'
}, {
  title: 'Considering alternative explanations',
  text: 'Other possible diagnoses and explanations for the presentation are considered and addressed, and any range of opinion is acknowledged.'
}, {
  title: 'Writing for legal readers',
  text: 'Psychiatric terms are explained in plain language for solicitors, barristers, judges and tribunal members.'
}];
const atAGlance = ['MBBS', 'MBA', 'Registered with the General Medical Council', 'Expert Witness training', 'Remote and face-to-face assessments'];
export default function About() {
  return <main className="min-h-screen bg-white" data-id="element-495" data-mp="3dfbv8heuouju">
      <SEO title="About Dr Imtiaz Syed | Independent Psychiatric Expert Witness" description="Dr Imtiaz Syed MBBS MBA, independent psychiatric expert witness for solicitors and barristers. GMC registered. Discuss an instruction." path="/about" keywords={['Dr Imtiaz Syed', 'psychiatric expert witness UK', 'medico-legal psychiatrist for solicitors', 'GMC registered expert witness']} imageAlt="Dr Imtiaz Syed, Independent Psychiatric Expert Witness" schema={aboutSchema} data-id="element-496" data-mp="3dbxrb4qjgdaa" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-497" data-mp="1otjr3sq78zpb">
        <div className="container mx-auto px-4" data-id="element-498" data-mp="1oss6fcy54mdt">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-499" data-mp="1aubqcvee1jj0">
            About Dr Imtiaz Syed
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-500" data-mp="1etkjpz5infe1">
            Independent Psychiatric Expert Witness
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-501" data-mp="3h0aavrmaogk">
        <div className="container mx-auto px-4" data-id="element-502" data-mp="3dvwr7s9igd8">
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-503" data-mp="3s12bxko3ddf8">
            <div className="lg:w-1/3" data-id="element-504" data-mp="3n30pcprgivz0">
              <div className="lg:sticky lg:top-24" data-id="element-505" data-mp="3n1z7o2fxrb8b">
                <img src="https://cdn.magicpatterns.com/uploads/vPTXagdmQWKZaxnhPCE2i5/linked_in.jpg" alt="Dr Imtiaz Syed, Independent Psychiatric Expert Witness, in a book-lined study" width="400" height="400" decoding="async" className="w-full rounded-lg shadow-md mb-6 object-cover" data-id="element-506" data-mp="5uvl4wtecb5o" />
                <div className="bg-gray-50 rounded-lg p-6 shadow-md" data-id="element-507" data-mp="3j15il5qrbqdu">
                  <h2 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-508" data-mp="3j3i4tv3jsmwp">
                    At a Glance
                  </h2>
                  <ul className="space-y-2 text-slate-dark" data-id="element-509" data-mp="2qkkxn36mnkv7">
                    {atAGlance.map(item => <li key={item} className="flex items-start" data-id="element-510" data-mp="2lmt64a9wa0rj">
                        <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-0.5 shrink-0" aria-hidden="true" data-id="element-511" data-mp="3ba5dq3hc8lll" />
                        <span data-id="element-512" data-mp="3f9ea7g3rixku">{item}</span>
                      </li>)}
                  </ul>
                  <div className="mt-6 flex flex-col gap-3" data-id="element-513" data-mp="3p1aj7m7e083u">
                    <Button variant="primary" href={callsToAction.discussInstruction.href} className="w-full text-center" data-id="element-514" data-mp="3p0sc1uj6s9h5">
                      {callsToAction.discussInstruction.label}
                    </Button>
                    <Button variant="secondary" href={callsToAction.requestFeeQuote.href} className="w-full text-center" data-id="element-515" data-mp="31aod40gy0xwh">
                      {callsToAction.requestFeeQuote.label}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-2/3" data-id="element-516" data-mp="261kr9qs15cnp">
              <p className="text-slate-dark text-lg mb-8" data-id="element-517" data-mp="264qhj9ceakl0">
                Dr Imtiaz Syed is an independent psychiatric expert witness. He
                prepares psychiatric assessments and medico-legal reports for
                solicitors, barristers, case managers and other professionals
                instructing on behalf of claimants, defendants and the court,
                including as a single joint expert.
              </p>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-518" data-mp="1ic9vknpje8gf">
                Qualifications and Registration
              </h2>
              <p className="text-slate-dark mb-8" data-id="element-519" data-mp="1dg1m9qy0t9h6">
                Dr Syed holds a Bachelor of Medicine and Bachelor of Surgery
                (MBBS) and a Master of Business Administration (MBA). He is
                registered with the General Medical Council and has completed
                Expert Witness training.
              </p>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-520" data-mp="2bniw5cwu67y8">
                Psychiatric and Medico-Legal Experience
              </h2>
              <p className="text-slate-dark mb-8" data-id="element-521" data-mp="2gp005hamaaj1">
                Dr Syed’s medical career is in psychiatry. His clinical
                experience of assessing and treating adults with a wide range of
                mental health conditions forms the basis of his medico-legal
                work, which covers criminal, civil, family, immigration,
                employment and Court of Protection proceedings.
              </p>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-522" data-mp="1jx4mxtzrpjsw">
                Areas of Medico-Legal Practice
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-523" data-mp="1nyhx2g9w895m">
                Dr Syed considers instructions in the following areas. Each
                enquiry is reviewed individually, and he will confirm whether a
                case falls within his expertise before accepting it.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-8" data-id="element-524" data-mp="16pf8lc21dd7k">
                {practiceAreas.map(area => <li key={area.path} className="flex items-start" data-id="element-525" data-mp="1gep2mxgfx5yb">
                    <span className="text-secondary font-bold mr-2" aria-hidden="true" data-id="element-526" data-mp="1gg060w5z4bow">
                      •
                    </span>
                    <Link to={area.path} className="text-primary underline hover:text-secondary transition-colors" data-id="element-527" data-mp="1qcmpphmkcwpc">
                      {area.label}
                    </Link>
                  </li>)}
              </ul>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-528" data-mp="1ueq52hccslfw">
                Approach to Assessment and Report Writing
              </h2>
              <p className="text-slate-dark mb-6" data-id="element-529" data-mp="4zsdlnk9agqy">
                Dr Syed’s overriding duty is to the court, not to the party who
                instructs him. His reports are independent, and he works within
                the duties set out in Part 35 of the Civil Procedure Rules, Part
                19 of the Criminal Procedure Rules and Part 25 of the Family
                Procedure Rules, as applicable to the proceedings.
              </p>
              <div className="space-y-4 mb-8" data-id="element-530" data-mp="2yeri8sofsa2r">
                {approach.map(item => <div key={item.title} className="bg-gray-50 p-5 rounded-lg" data-id="element-531" data-mp="3v50j4hd3tru7">
                    <h3 className="font-bold text-primary mb-1" data-id="element-532" data-mp="3r2vs7qe1j9b9">{item.title}</h3>
                    <p className="text-slate-dark" data-id="element-533" data-mp="3r505gyxtxko7">{item.text}</p>
                  </div>)}
              </div>
              <p className="text-slate-dark mb-8" data-id="element-534" data-mp="3hdc86held9et">
                Where a question falls outside his expertise, or the evidence
                does not allow a firm opinion, the report says so.
              </p>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-535" data-mp="dvu0c9ytk2ee">
                Assessment Options
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-536" data-mp="l930kxjlhgeu">
                The format is agreed for each case, depending on what is
                clinically appropriate and practical for the person being
                assessed:
              </p>
              <ul className="list-disc pl-6 mb-8 text-slate-dark space-y-2" data-id="element-537" data-mp="z7b3rn785dyg">
                <li data-id="element-538" data-mp="z56q4djix0kz">Remote assessment by secure video</li>
                <li data-id="element-539" data-mp="145cnmach5haz">Face-to-face assessment</li>
                <li data-id="element-540" data-mp="144jp1wno5y3x">Home and care setting visits</li>
                <li data-id="element-541" data-mp="194fix32nk95r">Prison and secure setting visits</li>
              </ul>

              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-542" data-mp="1dvnrjww5rqhv">
                Instructing Dr Syed
              </h2>
              <ul className="space-y-1 text-slate-dark font-medium mb-3" data-id="element-543" data-mp="1itz3cd60kb2q">
                <li data-id="element-544" data-mp="1mxna4tmtn2mx">{turnaround.urgent}</li>
                <li data-id="element-545" data-mp="1myowhu9afbjy">{turnaround.standard}</li>
              </ul>
              <p className="text-slate-dark mb-6" data-id="element-546" data-mp="1rx08aaj57w4t">{turnaround.caveat}</p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-547" data-mp="3nyts1f9oskig">
                <Button variant="primary" size="lg" href={callsToAction.discussInstruction.href} data-id="element-548" data-mp="3nx0mk3at86c1">
                  {callsToAction.discussInstruction.label}
                </Button>
                <Button variant="secondary" size="lg" href={callsToAction.requestFeeQuote.href} data-id="element-549" data-mp="1l3clxdfiqjk">
                  {callsToAction.requestFeeQuote.label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}